import React, { useEffect, useState, useCallback } from 'react';
import { db, functions } from '../firebase';
import {
  collection, getDocs, doc, updateDoc, query, orderBy, limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { SubNav, Metric, BarRow, KpiRow } from '../components/SharedComponents';

const BLOCK_LABELS = {
  hotell: 'Hotell', belaggning: 'Beläggning', brasserie: 'Brasserie', knallen: 'Knallen', sportcenter: 'Sportcenter',
  stugservice: 'Stugservice', lanthandel: 'Lanthandeln', ledning: 'Ledning', budget: 'Budget', admin: 'Admin',
};
const MODE_LABELS = {
  monthly: 'Månad', daily: 'Dag', weekly: 'Vecka', bookings: 'Bokningar',
  newbookings: 'Nya bokn.', topproducts: 'Produkter', ledning: 'Ledning', roomstats: 'Rumsstat.',
  daily_units: 'Dag×enhet', daily_hours: 'Dag×timmar', daily_brass_split: 'Brasserie dag/kväll',
  belaggning: 'Beläggning', lanthandel: 'Lanthandeln',
};
const fmtTs = (ms) => ms ? new Date(ms).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–';
const fmtAgo = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 3600) return `${Math.round(s / 60)} min sedan`;
  if (s < 86400) return `${Math.round(s / 3600)} h sedan`;
  return `${Math.round(s / 86400)} d sedan`;
};

const ALL_BLOCKS = [
  { id: 'hotell',      label: 'Hotell' },
  { id: 'belaggning',  label: 'Beläggning' },
  { id: 'brasserie',   label: 'Brasserie' },
  { id: 'knallen',     label: 'Knallen' },
  { id: 'sportcenter', label: 'Sportcenter' },
  { id: 'stugservice', label: 'Stugservice' },
  { id: 'lanthandel',  label: 'Lanthandeln' },
  { id: 'ledning',     label: 'Ledning' },
  // Budget kan delas ut utan adminbehörighet. Generatorn, fast/säsong-panelen
  // och Bokförings-fliken kräver ändå admin — de rör hela resultaträkningen
  // och individlöner.
  { id: 'budget',      label: 'Budget' },
];

const MASTER_EMAIL = 'magnus@bruksvallsliden.se'; // Skyddat master-konto

export default function AdminBlock({ currentUserEmail }) {
  const [users, setUsers]         = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null); // hopfällda rader — skydd mot felklick
  const [newEmail, setNewEmail]   = useState('');
  const [newPw, setNewPw]         = useState('');
  const [newBlocks, setNewBlocks] = useState([]);
  const [adding, setAdding]       = useState(false);
  const [addError, setAddError]   = useState('');
  const [addOk, setAddOk]         = useState('');
  const [feedback, setFeedback]   = useState([]);
  const [fbLoading, setFbLoading] = useState(true);
  const [view, setView]           = useState('users');
  const [activity, setActivity]   = useState(null);
  const [actLoading, setActLoading] = useState(false);
  const [system, setSystem]       = useState(null);
  const [sysLoading, setSysLoading] = useState(false);

  const load = useCallback(async () => {
    setLoadingUsers(true);
    const snap = await getDocs(collection(db, 'intel_users'));
    const list = snap.docs
      // Dokument-ID:t ÄR e-posten — fallback om email-fältet saknas (partiellt
      // skapad post, t.ex. avbruten första inloggning) så raden förblir hanterbar
      .map(d => ({ id: d.id, ...d.data(), email: d.data().email || d.id }))
      .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    setUsers(list);
    setLoadingUsers(false);
  }, []);

  const loadFeedback = useCallback(async () => {
    setFbLoading(true);
    try {
      const q = query(collection(db, 'intel_feedback'), orderBy('createdAt', 'desc'), limit(50));
      const snap = await getDocs(q);
      setFeedback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Feedback load error:', e);
    } finally {
      setFbLoading(false);
    }
  }, []);

  const loadActivity = useCallback(async () => {
    setActLoading(true);
    try {
      const fn = httpsCallable(functions, 'getActivityStats');
      const r = await fn();
      setActivity(r.data);
    } catch (e) { console.error('Activity load error:', e); }
    finally { setActLoading(false); }
  }, []);

  const loadSystem = useCallback(async () => {
    setSysLoading(true);
    try {
      const fn = httpsCallable(functions, 'getSystemStats');
      const r = await fn();
      setSystem(r.data);
    } catch (e) { console.error('System load error:', e); }
    finally { setSysLoading(false); }
  }, []);

  useEffect(() => { load(); loadFeedback(); }, [load, loadFeedback]);

  useEffect(() => {
    if (view === 'activity' && !activity) loadActivity();
    if (view === 'system'   && !system)   loadSystem();
  }, [view, activity, system, loadActivity, loadSystem]);

  const toggleAdmin = async (email, current) => {
    if (email === currentUserEmail || email === MASTER_EMAIL) return;
    const next = !current;
    if (next && !window.confirm(`Ge admin-behörighet till ${email}?`)) return;
    try {
      const fn = httpsCallable(functions, 'setUserAdmin');
      await fn({ email, isAdmin: next });
      setUsers(prev => prev.map(u => u.email === email ? { ...u, isAdmin: next } : u));
    } catch (e) {
      alert(e.message || 'Kunde inte ändra admin-behörighet.');
    }
  };

  const toggleBlock = async (email, blockId) => {
    const u = users.find(u => u.email === email);
    const cur  = u.blocks || [];
    const next = cur.includes(blockId) ? cur.filter(b => b !== blockId) : [...cur, blockId];
    await updateDoc(doc(db, 'intel_users', email), { blocks: next });
    setUsers(prev => prev.map(u => u.email === email ? { ...u, blocks: next } : u));
  };

  const addUser = async () => {
    setAdding(true); setAddError(''); setAddOk('');
    try {
      const fn = httpsCallable(functions, 'createExternalUser');
      await fn({ email: newEmail, password: newPw, blocks: newBlocks });
      setAddOk(`${newEmail} skapades.`);
      setNewEmail(''); setNewPw(''); setNewBlocks([]);
      await load();
    } catch (e) {
      setAddError(e.message || 'Något gick fel.');
    } finally { setAdding(false); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Ta bort ${email}?`)) return;
    try {
      const fn = httpsCallable(functions, 'deleteIntelUser');
      await fn({ email });
      await load();
    } catch (e) {
      alert(e.message || 'Kunde inte ta bort användaren.');
    }
  };

  // Aktivitet — sidvisningar
  const pv = activity?.pageviews || {};
  const pvEntries = Object.entries(pv).sort((a, b) => b[1] - a[1]);
  const pvMax = Math.max(...pvEntries.map(e => e[1]), 1);
  const pvTotal = pvEntries.reduce((s, e) => s + e[1], 0);

  // System — aggregera per läge över 14 dagar
  const sysDaily = system?.daily || [];
  const modeAgg = {};
  for (const d of sysDaily) {
    for (const [m, v] of Object.entries(d.modes || {})) {
      if (!modeAgg[m]) modeAgg[m] = { count: 0, ms: 0, rows: 0, errors: 0 };
      modeAgg[m].count  += v.count  || 0;
      modeAgg[m].ms     += v.ms     || 0;
      modeAgg[m].rows   += v.rows   || 0;
      modeAgg[m].errors += v.errors || 0;
    }
  }
  const modeRows = Object.entries(modeAgg).sort((a, b) => b[1].count - a[1].count);
  const refreshBtn = { fontSize: 12, fontFamily: 'system-ui', background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', color: '#555' };
  const muted = { fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' };

  return (
    <div className="screen">
      <div className="header header-ledning" style={{ marginBottom: '1rem' }}>
        <div>
          <div className="header-title">Administration</div>
          <div className="header-name">Användare &amp; Behörigheter</div>
        </div>
      </div>

      <SubNav
        tabs={[
          { id: 'users',    label: 'Användare' },
          { id: 'activity', label: 'Aktivitet' },
          { id: 'system',   label: 'System'    },
        ]}
        active={view}
        onChange={setView}
      />

      {view === 'users' && (<>
      {/* ── Användarlista ── */}
      <div className="card">
        <div className="card-title">Aktiva användare</div>
        {loadingUsers ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar...</p>
        ) : (
          <div>
            {users.map(u => {
              const isSelf = u.email === currentUserEmail;
              const isMaster = u.email === MASTER_EMAIL;
              const open = expandedUser === u.email;
              const sammanfattning = u.isAdmin
                ? 'Admin — ser allt'
                : (u.blocks || []).length
                  ? ALL_BLOCKS.filter(b => (u.blocks || []).includes(b.id)).map(b => b.label).join(' · ')
                  : 'Inga behörigheter';
              return (
                <div key={u.email} style={{ borderTop: '1px solid var(--border)' }}>
                  {/* Hopfälld rad — klick fäller ut behörigheterna (skydd mot felklick) */}
                  <div onClick={() => setExpandedUser(open ? null : u.email)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 2px', cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none', fontSize: 11, color: 'var(--text-secondary)' }}>▸</span>
                      <span className="admin-user-email">{u.email}</span>
                      <span className={`badge ${u.provider === 'google' ? 'badge-blue' : 'badge-amber'}`}>
                        {u.provider === 'google' ? 'Google' : 'Lösenord'}
                      </span>
                      {isMaster && <span className="badge badge-green">🔒 Master</span>}
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '46%', flexShrink: 0 }}>
                      {sammanfattning}
                    </span>
                  </div>

                  {open && (
                    <div style={{ padding: '2px 2px 12px 22px', display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
                      {ALL_BLOCKS.map(b => (
                        <label key={b.id} style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: u.isAdmin ? 'default' : 'pointer', color: 'var(--text)' }}>
                          {u.isAdmin ? (
                            <span style={{ color: '#3B6D11', fontSize: 13 }}>✓</span>
                          ) : (
                            <input
                              type="checkbox"
                              className="admin-checkbox"
                              checked={(u.blocks || []).includes(b.id)}
                              onChange={() => toggleBlock(u.email, b.id)}
                            />
                          )}
                          {b.label}
                        </label>
                      ))}
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12.5, fontWeight: 700, cursor: (isSelf || isMaster) ? 'default' : 'pointer', color: 'var(--text)' }}
                        title={isMaster ? 'Master-kontot är alltid admin' : isSelf ? 'Kan inte ändra din egen admin-behörighet' : (u.isAdmin ? 'Ta bort admin' : 'Ge admin')}>
                        <input
                          type="checkbox"
                          className="admin-checkbox"
                          checked={!!u.isAdmin}
                          disabled={isSelf || isMaster}
                          onChange={() => toggleAdmin(u.email, u.isAdmin)}
                        />
                        Admin
                      </label>
                      {!isSelf && !isMaster && (
                        <button className="admin-delete-btn" onClick={() => deleteUser(u.email)}>
                          Ta bort
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Lägg till extern ── */}
      <div className="card">
        <div className="card-title">Lägg till extern användare</div>
        <div className="admin-add-form">
          <input
            className="login-input"
            type="email"
            placeholder="E-postadress"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
          />
          <input
            className="login-input"
            type="password"
            placeholder="Tillfälligt lösenord"
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
          />
          <div className="admin-block-select">
            <div className="admin-block-select-label">Åtkomst till</div>
            {ALL_BLOCKS.map(b => (
              <label key={b.id} className="admin-block-label">
                <input
                  type="checkbox"
                  checked={newBlocks.includes(b.id)}
                  onChange={() =>
                    setNewBlocks(prev =>
                      prev.includes(b.id) ? prev.filter(x => x !== b.id) : [...prev, b.id]
                    )
                  }
                />
                {b.label}
              </label>
            ))}
          </div>
          {addError && <div className="login-error">{addError}</div>}
          {addOk    && <div className="admin-ok">{addOk}</div>}
          <button
            className="login-btn"
            onClick={addUser}
            disabled={adding || !newEmail || !newPw}
            style={{ marginTop: 12 }}
          >
            {adding ? 'Skapar...' : 'Skapa användare'}
          </button>
        </div>
      </div>

      {/* ── Feedback ── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="card-title" style={{ margin: 0 }}>Feedback &amp; Idéer</div>
          <button
            onClick={loadFeedback}
            style={{ fontSize: 12, fontFamily: 'system-ui', background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', color: '#555' }}
          >
            Uppdatera
          </button>
        </div>
        {fbLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar...</p>
        ) : feedback.length === 0 ? (
          <p style={{ fontSize: 13, color: '#aaa', fontFamily: 'system-ui,sans-serif' }}>Ingen feedback ännu.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {feedback.map(fb => {
              const ts = fb.createdAt?.toDate?.();
              const dateStr = ts ? ts.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={fb.id} style={{ padding: '12px 14px', background: 'var(--panel)', borderRadius: 8, fontFamily: 'system-ui,sans-serif' }}>
                  <div style={{ fontSize: 13, color: '#1B2A4A', lineHeight: 1.5 }}>{fb.text}</div>
                  <div style={{ marginTop: 6, fontSize: 11, color: '#aaa' }}>
                    {fb.email}
                    {dateStr && <span style={{ marginLeft: 8 }}>· {dateStr}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>)}

      {/* ── AKTIVITET ── */}
      {view === 'activity' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button onClick={loadActivity} style={refreshBtn}>Uppdatera</button>
          </div>
          {actLoading && !activity ? (
            <div className="card"><p style={muted}>Laddar aktivitet...</p></div>
          ) : (
            <>
              <div className="grid-3">
                <Metric label="Aktiva användare" value={activity?.active7 ?? '–'}  sub="Senaste 7 dagarna" />
                <Metric label="Aktiva användare" value={activity?.active30 ?? '–'} sub="Senaste 30 dagarna" />
                <Metric label="Sidvisningar"     value={pvTotal.toLocaleString('sv-SE')} sub="Totalt (alla sidor)" />
              </div>
              <div className="card">
                <div className="card-title">Engagemang per användare</div>
                {(activity?.users || []).length === 0 ? (
                  <p style={muted}>Ingen användardata ännu.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui,sans-serif' }}>
                      <thead><tr>
                        {['Användare', 'Senaste aktivitet', 'Inloggn. 30d', 'Sidvisn.', 'Aktiva dagar 30d', 'Mest använd'].map((h, i) => (
                          <th key={i} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.03em', padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {activity.users.map((u) => (
                          <tr key={u.email}>
                            <td style={{ fontSize: 13, padding: '7px 10px', borderBottom: '1px solid var(--border)' }}>{u.email}</td>
                            <td style={{ fontSize: 13, padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>{u.lastSeen ? fmtTs(u.lastSeen) : '–'} {u.lastSeen ? <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>({fmtAgo(u.lastSeen)})</span> : null}</td>
                            <td style={{ fontSize: 13, padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>{u.logins30 || '–'}</td>
                            <td style={{ fontSize: 13, padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>{u.views || '–'}</td>
                            <td style={{ fontSize: 13, padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: 700, color: u.activeDays30 >= 10 ? '#1D9E75' : u.activeDays30 > 0 ? 'var(--text)' : 'var(--text-secondary)' }}>{u.activeDays30 || '–'}</td>
                            <td style={{ fontSize: 13, padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{u.topBlock ? (BLOCK_LABELS[u.topBlock] || u.topBlock) : '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="note">Sorterat efter aktiva dagar. Inloggn. + senaste aktivitet finns historiskt; sidvisn./aktiva dagar/sektion byggs upp från nu (per-användare-loggning).</div>
              </div>
              <div className="card">
                <div className="card-title">Mest besökta sidor</div>
                {pvEntries.length === 0 ? (
                  <p style={muted}>Ingen sidvisningsdata ännu.</p>
                ) : pvEntries.map(([block, n]) => (
                  <BarRow key={block} label={BLOCK_LABELS[block] || block} value={n} max={pvMax} color="#1B2A4A" formatted={n.toLocaleString('sv-SE')} />
                ))}
                <div className="note">Aggregerat — ingen koppling till enskild användare</div>
              </div>
              <div className="card">
                <div className="card-title">🎩 Fråga Birger — användning</div>
                {!activity?.birger ? (
                  <p style={muted}>Inga Birger-frågor loggade ännu (statistiken byggs upp från nu).</p>
                ) : (
                  <>
                    <div className="grid-3" style={{ marginBottom: 10 }}>
                      <Metric label="Frågor totalt" value={activity.birger.total.toLocaleString('sv-SE')} sub="Sedan start" />
                      <Metric label="Senaste 7 dagarna" value={activity.birger.last7.toLocaleString('sv-SE')} sub="Frågor" />
                      <Metric label="Senaste 30 dagarna" value={activity.birger.last30.toLocaleString('sv-SE')} sub="Frågor" />
                    </div>
                    {activity.birger.perUser.map((p) => (
                      <BarRow key={p.email} label={p.email} value={p.fragor}
                        max={activity.birger.perUser[0]?.fragor || 1} color="#4A3B2A"
                        formatted={p.fragor.toLocaleString('sv-SE')} />
                    ))}
                    <div className="note">Antal ställda frågor per konto. Frågornas innehåll loggas aldrig — bara att de ställts.</div>
                  </>
                )}
              </div>
              <div className="card">
                <div className="card-title">Senaste inloggningar</div>
                {(activity?.recentLogins || []).length === 0 ? (
                  <p style={muted}>Inga inloggningar loggade ännu.</p>
                ) : activity.recentLogins.map((l, i) => (
                  <KpiRow key={i} label={l.email} value={fmtTs(l.ts)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── SYSTEM ── */}
      {view === 'system' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button onClick={loadSystem} style={refreshBtn}>Uppdatera</button>
          </div>
          {sysLoading && !system ? (
            <div className="card"><p style={muted}>Laddar systemstatus...</p></div>
          ) : (
            <>
              <div className="card">
                <div className="card-title">Qlik-hämtningar per läge — senaste 14 dagarna</div>
                <div className="period-table">
                  <div className="period-header period-row-5">
                    <span>Läge</span><span>Anrop</span><span>Snitt-ms</span><span>Rader</span><span>Fel</span>
                  </div>
                  {modeRows.length === 0 ? (
                    <p style={{ ...muted, padding: '8px 0' }}>Ingen data ännu — hämtningar loggas framöver.</p>
                  ) : modeRows.map(([m, v]) => (
                    <div key={m} className="period-row period-row-5">
                      <span>{MODE_LABELS[m] || m}</span>
                      <span>{v.count.toLocaleString('sv-SE')}</span>
                      <span>{v.count ? Math.round(v.ms / v.count).toLocaleString('sv-SE') : '–'}</span>
                      <span>{v.rows.toLocaleString('sv-SE')}</span>
                      <span style={{ color: v.errors > 0 ? '#A32D2D' : 'inherit' }}>{v.errors}</span>
                    </div>
                  ))}
                </div>
                <div className="note">Källa: Cloud Functions — getQlikData (tid, antal rader, fel per anrop)</div>
              </div>
              <div className="card">
                {(() => {
                  const errs = system?.errors || [];
                  const active = errs.filter(e => !e.resolved);
                  return (
                    <>
                      <div className="card-title">
                        Senaste fel
                        {errs.length > 0 && (
                          <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 8, color: active.length ? '#A32D2D' : '#1D9E75' }}>
                            {active.length ? `· ${active.length} aktiva` : '· allt åtgärdat 🟢'}
                          </span>
                        )}
                      </div>
                      {errs.length === 0 ? (
                        <p style={muted}>Inga fel loggade. 🟢</p>
                      ) : errs.map((e, i) => (
                        <div key={i} style={{ padding: '10px 12px', background: e.resolved ? '#F2F1ED' : '#FCEBEB', borderRadius: 8, marginBottom: 8, fontFamily: 'system-ui,sans-serif', opacity: e.resolved ? 0.6 : 1 }}>
                          <div style={{ fontSize: 12, color: e.resolved ? '#777' : '#A32D2D', fontWeight: 600 }}>
                            {e.resolved ? '✓ ' : '⚠ '}{MODE_LABELS[e.mode] || e.mode} · {fmtTs(e.ts)}{e.ts ? ` (${fmtAgo(e.ts)})` : ''}{e.ms != null ? ` · ${e.ms} ms` : ''}
                            {e.resolved ? <span style={{ color: '#1D9E75', fontWeight: 600 }}> · åtgärdat</span> : null}
                          </div>
                          <div style={{ fontSize: 12, color: '#555', marginTop: 3, wordBreak: 'break-word' }}>{e.error}</div>
                          {e.stack ? (
                            <pre style={{ fontSize: 11, color: '#777', marginTop: 5, marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace,Menlo,Consolas,monospace' }}>{e.stack}</pre>
                          ) : null}
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
